import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Refresh,
  Home,
  BugReport,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { logger } from "@/utils/logger";
import { cn } from "@/lib/utils";

interface Chat8ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface Chat8ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
  timestamp?: Date;
}

export class Chat8ErrorBoundary extends Component<
  Chat8ErrorBoundaryProps,
  Chat8ErrorBoundaryState
> {
  constructor(props: Chat8ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Chat8ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorId: `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log detalhado do erro
    logger.error("[Chat8ErrorBoundary] Error caught:", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: this.state.timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    this.setState({ error, errorInfo });

    // Callback personalizado se fornecido
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log para monitoramento em desenvolvimento
    if (process.env.NODE_ENV === "development") {
      console.group("🚨 Chat8 Error Boundary Details");
      console.error("Error:", error);
      console.error("Error Info:", errorInfo);
      console.error("Error ID:", this.state.errorId);
      console.error("Timestamp:", this.state.timestamp);
      console.groupEnd();
    }

    // Reportar erro para serviços de monitoramento (se configurado)
    this.reportError(error, errorInfo);
  }

  reportError = (error: Error, errorInfo: ErrorInfo) => {
    const errorDetails = {
      errorId: this.state.errorId,
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: this.state.timestamp,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Copiar detalhes para clipboard
    navigator.clipboard
      .writeText(JSON.stringify(errorDetails, null, 2))
      .then(() => {
        alert(
          "Detalhes do erro copiados para a área de transferência. Por favor, envie para o suporte.",
        );
      })
      .catch(() => {
        console.log("Error details:", errorDetails);
        alert(
          "Detalhes do erro logados no console. Por favor, envie para o suporte.",
        );
      });
  };

  handleRetry = () => {
    logger.info("[Chat8ErrorBoundary] Attempting to recover from error");
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: undefined,
      timestamp: undefined,
    });
  };

  handleReload = () => {
    logger.info("[Chat8ErrorBoundary] Reloading page due to error");
    window.location.reload();
  };

  handleGoHome = () => {
    logger.info("[Chat8ErrorBoundary] Navigating to home due to error");
    window.location.href = "/";
  };

  handleReportBug = () => {
    if (this.state.error && this.state.errorInfo) {
      this.reportError(this.state.error, this.state.errorInfo);
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div 
          className="bg-[#0a0a1a] text-slate-200 p-8 m-4 rounded-2xl border-2 border-red-500 text-center min-h-[500px] flex flex-col justify-center items-center gap-6 shadow-[0_8px_32px_rgba(239,68,68,0.2)] relative overflow-hidden"
          role="alert" 
          aria-live="polite"
        >
          {/* Shimmer animation */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-red-500 animate-[shimmer_2s_ease-in-out_infinite]"></div>
          
          <div className="text-red-500 font-bold mb-4 text-3xl flex items-center gap-2">
            <ErrorIcon className="text-3xl" />
            Erro no Chat Inteligente
          </div>

          <Alert severity="error" className="max-w-[600px] mb-4">
            <AlertTitle>Erro Detectado</AlertTitle>
            Ocorreu um erro inesperado no sistema de chat. Nossa equipe foi
            notificada e está trabalhando para resolver o problema.
          </Alert>

          <div className="text-slate-400 mb-6 max-w-[600px] leading-relaxed text-lg">
            Se o problema persistir, tente recarregar a página ou entre em
            contato com o suporte.
            <br />
            <strong>ID do Erro:</strong> {this.state.errorId}
          </div>

          <div className="flex gap-4 flex-wrap justify-center mb-4">
            <Button
              onClick={this.handleRetry}
              variant="contained"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-6 py-2 font-semibold text-base min-w-[140px] normal-case"
              startIcon={<Refresh />}
              aria-label="Tentar novamente"
            >
              Tentar Novamente
            </Button>

            <Button
              onClick={this.handleReload}
              variant="outlined"
              className="border-gray-600 text-slate-400 hover:border-gray-500 hover:bg-slate-800 rounded-xl px-6 py-2 font-semibold text-base min-w-[140px] normal-case"
              startIcon={<Refresh />}
              aria-label="Recarregar página"
            >
              Recarregar
            </Button>

            <Button
              onClick={this.handleGoHome}
              variant="outlined"
              className="border-gray-600 text-slate-400 hover:border-gray-500 hover:bg-slate-800 rounded-xl px-6 py-2 font-semibold text-base min-w-[140px] normal-case"
              startIcon={<Home />}
              aria-label="Ir para página inicial"
            >
              Página Inicial
            </Button>

            <Button
              onClick={this.handleReportBug}
              variant="outlined"
              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-xl px-6 py-2 font-semibold text-base min-w-[140px] normal-case"
              startIcon={<BugReport />}
              aria-label="Reportar bug"
            >
              Reportar Bug
            </Button>
          </div>

          <div className="flex gap-4 mt-4 p-4 bg-slate-800 rounded-lg border border-slate-600 text-sm text-slate-400">
            <div className="flex flex-col items-center gap-1">
              <span>ID do Erro</span>
              <span className="text-slate-200 font-semibold">{this.state.errorId}</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <span>Horário</span>
              <span className="text-slate-200 font-semibold">
                {this.state.timestamp?.toLocaleTimeString()}
              </span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <span>Data</span>
              <span className="text-slate-200 font-semibold">
                {this.state.timestamp?.toLocaleDateString()}
              </span>
            </div>
          </div>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 mt-6 max-w-full overflow-auto text-left max-h-[300px]">
              <div className="text-red-500 mb-4 text-lg font-semibold">
                🔍 Detalhes do Erro (Desenvolvimento)
              </div>
              <pre className="text-slate-200 text-sm m-0 whitespace-pre-wrap break-words font-mono">
                <div className="text-red-200 mb-2">
                  <strong>Mensagem:</strong> {this.state.error.message}
                </div>
                <div className="text-red-300 font-semibold">
                  <strong>Stack Trace:</strong>
                  {this.state.error.stack}
                </div>
                {this.state.errorInfo && (
                  <>
                    <div className="text-red-300 font-semibold">
                      <strong>Component Stack:</strong>
                      {this.state.errorInfo.componentStack}
                    </div>
                  </>
                )}
              </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook para usar Error Boundary em componentes funcionais
export const useChat8ErrorBoundary = () => {
  const [hasError, setHasError] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logger.error("[useChat8ErrorBoundary] Error caught:", event.error);
      setError(event.error);
      setHasError(true);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error(
        "[useChat8ErrorBoundary] Unhandled promise rejection:",
        event.reason,
      );
      setError(
        new Error(event.reason?.message || "Unhandled Promise Rejection"),
      );
      setHasError(true);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  const resetError = () => {
    setHasError(false);
    setError(null);
  };

  return { hasError, error, resetError };
};

export default Chat8ErrorBoundary;
